import {ApolloLink, type FetchResult, Observable} from '@apollo/client';

import {getRumViewStartTimestamp} from './datadog/datadogRumTimings';
import {startupGraphqlQueryDuration} from './datadog/perfObserver/marks/startupGraphqlMarks';

const MAX_STARTUP_GRAPHQL_OPERATIONS = 100;
const STARTUP_GRAPHQL_WINDOW_MS = 30_000;
const recordedOperationNames = new Set<string>();

export const startupGraphqlTimingLink = new ApolloLink((operation, forward) => {
  const name = operation.operationName;
  if (!name) {
    return forward(operation);
  }

  const startupCheckTime = performance.now();
  const rumViewStartTimestamp = getRumViewStartTimestamp();
  if (
    rumViewStartTimestamp !== performance.timeOrigin ||
    startupCheckTime >= STARTUP_GRAPHQL_WINDOW_MS ||
    recordedOperationNames.has(name) ||
    recordedOperationNames.size >= MAX_STARTUP_GRAPHQL_OPERATIONS
  ) {
    return forward(operation);
  }

  recordedOperationNames.add(name);
  const startTime = performance.now();
  const scopeStartTime = getRumViewStartTimestamp();
  const operationKey = `startup_graphql_${name}`;
  const graphqlContext = {
    operation: name,
    start_ms: Math.round(startTime),
  };

  try {
    startupGraphqlQueryDuration.recordStartPerfMark({
      context: {graphql: graphqlContext},
      operationKey,
      scopeStartTime,
      startTime,
    });
  } catch (error) {
    console.error(
      'Error while trying to do startup GraphQL perf logging',
      error
    );
  }

  return new Observable<FetchResult>(observer => {
    let ended = false;
    const recordEnd = (outcome: 'success' | 'error') => {
      if (ended) {
        return;
      }
      ended = true;
      try {
        startupGraphqlQueryDuration.recordEndPerfMark({
          context: {graphql: {...graphqlContext, outcome}},
          operationKey,
          scopeStartTime,
          startTime: performance.now(),
        });
      } catch (error) {
        console.error(
          'Error while trying to do startup GraphQL perf logging',
          error
        );
      }
    };

    const subscription = forward(operation).subscribe({
      next: result => {
        recordEnd('success');
        observer.next(result);
      },
      error: error => {
        recordEnd('error');
        observer.error(error);
      },
      complete: () => observer.complete(),
    });

    return () => subscription.unsubscribe();
  });
});
